import { Endpoint, Params, QueryConfig, Request, RequestTypes } from '~/models/request';
import { formatEndpoint, handleError, request } from '~/utils/api/requests';
import { QueryClient, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { useCallback } from 'react';

export const useCancelQuery = () => {
	const queryClient = useQueryClient();

	const invalidate = useCallback(
		(endpoint: Endpoint | string[], params?: Params) => {
			if (!endpoint) return;

			let key;

			if (!Array.isArray(endpoint)) {
				key = formatEndpoint(endpoint, params)?.key ?? [];
			} else {
				key = endpoint;
			}

			return queryClient.cancelQueries({ queryKey: key });
		},
		[queryClient]
	);

	return invalidate;
};

export const useInvalidateQuery = () => {
	const queryClient = useQueryClient();

	const invalidate = useCallback(
		(endpoint: Endpoint, params?: Params) => {
			const key = formatEndpoint(endpoint, params)?.key ?? [];
			return queryClient.invalidateQueries(key);
		},
		[queryClient]
	);

	return invalidate;
};

// export const queryHandler =
// 	<Data = unknown>(queryContext: RootQueryContext, queryFn: (endpoint: Endpoint, config: QueryConfig<Data> | undefined, context: RootQueryContext) => UseQueryOptions<Data, any, Data>) =>
// 	async (endpoint: Endpoint, config?: QueryConfig<Data>) => {
// 		const query = queryFn(endpoint, config, queryContext);
// 		const { queryClient } = queryContext;
//
// 		const data = (queryClient.getQueryData(query.queryKey ?? []) as Data | undefined) ?? (await queryClient.fetchQuery(query));
//
// 		return data;
// 	};

export const get = <QueryData = unknown, Data = QueryData, Error = void>(
	endpoint: Endpoint,
	config: QueryConfig<QueryData, Data, Error> | undefined
) =>
	queryWrapper<QueryData, Data, Error>('GET', {
		endpoint,
		config: config ?? {},
	});

export const del = <QueryData = unknown, Data = QueryData, Error = void>(
	endpoint: Endpoint,
	config: QueryConfig<QueryData, Data, Error> | undefined
) =>
	queryWrapper<QueryData, Data, Error>('DELETE', {
		endpoint,
		config: config ?? {},
	});

export const put = <QueryData = unknown, Data = QueryData, Error = void>(
	endpoint: Endpoint,
	config: QueryConfig<QueryData, Data, Error> | undefined
) =>
	queryWrapper<QueryData, Data, Error>('PUT', {
		endpoint,
		config: config ?? {},
	});

export const post = <QueryData = unknown, Data = QueryData, Error = void>(
	endpoint: Endpoint,
	config: QueryConfig<QueryData, Data, Error> | undefined
) =>
	queryWrapper<QueryData, Data, Error>('POST', {
		endpoint,
		config: config ?? {},
	});

const queryWrapper = <QueryData = unknown, Data = QueryData, Error = void>(
	type: RequestTypes,
	r: Request<void, Error, UseQueryOptions<QueryData, Error, Data>>
): UseQueryOptions<QueryData, Error, Data> => {
	const key = r.config.queryKey ?? formatEndpoint(r.endpoint, r.config?.params)?.key ?? [];
	const onError = handleError<void, Error>(r.config);

	return {
		queryKey: key,
		onError: (...args) => {
			onError(...args);
			r.config?.onError?.(...args);
		},
		networkMode: process.env.NODE_ENV === 'development' ? 'always' : undefined,
		queryFn: async ({ signal }) => {
			return await request<QueryData>({
				endpoint: r.endpoint,
				config: {
					requestConfig: r.config.requestConfig,
					settings: {
						signal,
						type,
						...r.config.settings,
					},
					params: r.config.params ?? {},
					data: r.config.data ?? {},
				},
			});
		},
		...r.config,
	};
};
